import React, { useEffect, useState, useCallback } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  SelectChangeEvent,
  CircularProgress,
  Alert,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Chip,
  TextField,
} from '@mui/material';
import { supabase } from '../lib/supabase';
import { DownloadIcon } from '../components/icons';
import { useAuth } from '../contexts/AuthContext';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
    ResponsiveContainer,
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    TooltipProps
} from 'recharts';
import { ValueType, NameType } from 'recharts/types/component/DefaultTooltipContent';
import { unparse } from 'papaparse';
import {
    startOfDay,
    endOfDay,
    subDays,
    startOfWeek,
    endOfWeek,
    startOfMonth,
    endOfMonth,
    subMonths,
    startOfYear,
    endOfYear,
    subYears
} from 'date-fns';

type UsageTrend = {
  cluster_id: string;
  clusters?: { name: string };
  period_type: string;
  period_start: string;
  period_end: string;
  data_consumption: number;
  airtime_consumption: number;
  sim_count: number;
};

type ClusterFilterItem = {
  id: string;
  name: string;
};

type PresetRange = 
    'today' | 'yesterday' | 
    'last7days' | 'last30days' | 
    'thisWeekSun' | 'lastWeekSun' | 
    'thisWeekMon' | 'lastWeekMon' |
    'thisMonth' | 'lastMonth' |
    'thisYear' | 'lastYear' | 
    'custom';

type AggregatedSummary = {
  cluster_id: string;
  cluster_name: string;
  total_data_consumption: number;
  total_airtime_consumption: number;
  total_sim_days: number;
  sim_count_latest: number;
};

type ChartDataPoint = {
  period_start_formatted: string;
  total_data: number;
  total_airtime: number;
};

// Type for Organization filter dropdown
type OrganizationFilterItem = {
  id: string;
  name: string;
};

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

export function Reports() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const { profile } = useAuth(); // Get user profile including role
  const userRole = profile?.role;
  const organizationId = profile?.organization_id;

  // Filters State
  const thirtyDaysAgo = startOfDay(subDays(new Date(), 29));
  const todayEnd = endOfDay(new Date());
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([thirtyDaysAgo, todayEnd]);
  const [selectedPreset, setSelectedPreset] = useState<PresetRange>('last30days');
  
  // Organization Filter State (for superadmin)
  const [allOrganizations, setAllOrganizations] = useState<OrganizationFilterItem[]>([]);
  const [selectedOrganizationIds, setSelectedOrganizationIds] = useState<string[]>([]); // Empty means all for superadmin

  // Cluster Filter State
  const [allClusters, setAllClusters] = useState<ClusterFilterItem[]>([]);
  const [selectedClusterIds, setSelectedClusterIds] = useState<string[]>([]); 

  // Data State
  const [trendData, setTrendData] = useState<UsageTrend[]>([]);
  const [summaryData, setSummaryData] = useState<AggregatedSummary[]>([]);
  const [chartData, setChartData] = useState<ChartDataPoint[]>([]);

  // Fetch Organizations for superadmin filter
  const fetchOrganizationsForFilter = useCallback(async () => {
      if (userRole !== 'superadmin') return;
      try {
          const { data, error: orgError } = await supabase
              .from('organizations')
              .select('id, name')
              .order('name');
          if (orgError) throw orgError;
          setAllOrganizations(data || []);
      } catch (err: any) {
          console.error('Error fetching organizations for filter:', err);
          setError('Failed to load organizations for filtering.');
      }
  }, [userRole]); // Depend on userRole

  // Fetch Clusters - now depends on selected organizations for superadmin
  const fetchClustersForFilter = useCallback(async () => {
    // For non-superadmin, fetch clusters for their org
    if (userRole !== 'superadmin' && organizationId) {
        try {
            const { data, error: clusterError } = await supabase
                .from('clusters')
                .select('id, name')
                .eq('organization_id', organizationId)
                .order('name');
            if (clusterError) throw clusterError;
            setAllClusters(data || []);
        } catch (err: any) {
            console.error('Error fetching clusters for filter (admin):', err);
            setError('Failed to load clusters for filtering.');
        }
    } 
    // For superadmin, fetch based on selected organizations
    else if (userRole === 'superadmin') {
        try {
            let query = supabase.from('clusters').select('id, name');
            // If specific orgs selected, filter clusters by those orgs
            if (selectedOrganizationIds.length > 0) {
                query = query.in('organization_id', selectedOrganizationIds);
            }
            // If no specific orgs selected, fetch all clusters (superadmin access)
            const { data, error: clusterError } = await query.order('name');
            if (clusterError) throw clusterError;
            setAllClusters(data || []);
        } catch (err: any) {
            console.error('Error fetching clusters for filter (superadmin):', err);
            setError('Failed to load clusters based on organization selection.');
        }
    } else {
        // Clear if no role or orgId (shouldn't happen if logged in)
        setAllClusters([]);
    }
  }, [userRole, organizationId, selectedOrganizationIds]); // Depend on role, orgId, selected orgs

  const calculateSummary = useCallback((trends: UsageTrend[]): AggregatedSummary[] => {
    const summaryMap = new Map<string, AggregatedSummary>();

    trends.forEach(trend => {
      const clusterId = trend.cluster_id;
      let existing = summaryMap.get(clusterId);

      const start = new Date(trend.period_start);
      const end = new Date(trend.period_end);
      const daysInPeriod = Math.max(1, Math.round((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)));

      if (!existing) {
        existing = {
          cluster_id: clusterId,
          cluster_name: trend.clusters?.name || 'Unknown Cluster',
          total_data_consumption: 0,
          total_airtime_consumption: 0,
          total_sim_days: 0,
          sim_count_latest: 0,
        };
      }

      existing.total_data_consumption += trend.data_consumption || 0;
      existing.total_airtime_consumption += trend.airtime_consumption || 0;
      existing.total_sim_days += (trend.sim_count || 0) * daysInPeriod;
      existing.sim_count_latest = trend.sim_count || 0;

      summaryMap.set(clusterId, existing);
    });

    return Array.from(summaryMap.values());
  }, []);

  const prepareChartData = useCallback((trends: UsageTrend[]): ChartDataPoint[] => {
    const aggregatedByPeriod = new Map<string, { data: number, airtime: number }>();

    trends.forEach(trend => {
      const periodKey = trend.period_start;
      let existing = aggregatedByPeriod.get(periodKey);
      if (!existing) {
        existing = { data: 0, airtime: 0 };
      }
      existing.data += trend.data_consumption || 0;
      existing.airtime += trend.airtime_consumption || 0;
      aggregatedByPeriod.set(periodKey, existing);
    });

    const formattedChartData = Array.from(aggregatedByPeriod.entries())
      .map(([period_start, totals]) => ({
        period_start_formatted: new Date(period_start).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }),
        total_data: parseFloat(totals.data.toFixed(2)),
        total_airtime: parseFloat(totals.airtime.toFixed(2)),
        _original_date: new Date(period_start)
      }))
      .sort((a, b) => a._original_date.getTime() - b._original_date.getTime());

    return formattedChartData;
  }, []);

  // Fetch Report Data - now filters by organization for superadmin if selected
  const fetchReportData = useCallback(async () => {
    const startDate = dateRange[0];
    const endDate = dateRange[1];

    const requiresOrgContext = userRole !== 'superadmin' && !organizationId;
    if (requiresOrgContext || !startDate || !endDate || startDate > endDate) {
      setError('Organization context not found, or invalid date range selected.');
      setLoading(false);
      setTrendData([]); 
      setSummaryData([]);
      setChartData([]);
      return;
    }
      setLoading(true);
      setError(null);
    try {
      // Fetch daily records, joining cluster name and organization_id
      // Use inner join to ensure cluster exists
      let query = supabase
        .from('usage_trends')
        .select('*, clusters!inner(name, organization_id)') 
        .eq('period_type', 'day') 
        .gte('period_start', startDate.toISOString()) 
        .lte('period_start', endDate.toISOString());

      // Apply Organization Filter (only for superadmin with specific selection)
      if (userRole === 'superadmin' && selectedOrganizationIds.length > 0) {
          query = query.in('clusters.organization_id', selectedOrganizationIds);
      }
      // Apply Cluster Filter (if any selected, regardless of role)
      if (selectedClusterIds.length > 0) {
        query = query.in('cluster_id', selectedClusterIds);
      }
      // Note: For non-superadmin, RLS on 'usage_trends' or 'clusters' must enforce organization boundary.
      // If RLS is insufficient, add: else if (userRole !== 'superadmin') { query = query.eq('clusters.organization_id', organizationId) }

      const { data, error: fetchError } = await query.order('period_start', { ascending: true }); 

      if (fetchError) throw fetchError;
      
      // Need to adjust type assertion if data includes clusters object
      const fetchedTrends = (data?.map(d => ({ ...d, clusters: d.clusters as { name: string, organization_id: string } })) || []) as UsageTrend[];

      setTrendData(fetchedTrends); 
      setSummaryData(calculateSummary(fetchedTrends)); 
      setChartData(prepareChartData(fetchedTrends)); 

    } catch (err: any) {
      console.error('Error fetching report data:', err);
      setError(err.message || 'Failed to load report data.');
      setTrendData([]);
      setSummaryData([]);
      setChartData([]); 
    } finally {
      setLoading(false);
    }
  }, [
      userRole, 
      organizationId, 
      dateRange, 
      selectedOrganizationIds, // Add dependency
      selectedClusterIds, 
      calculateSummary, 
      prepareChartData
  ]);

  // Initial data fetch - fetch orgs first for superadmin
  useEffect(() => {
      if (userRole === 'superadmin') {
          fetchOrganizationsForFilter();
      }
  }, [userRole, fetchOrganizationsForFilter]);

  // Fetch clusters when role/orgId or selectedOrganizations change
  useEffect(() => {
      // Clear selected clusters when available clusters change to avoid mismatches
      setSelectedClusterIds([]); 
      fetchClustersForFilter();
  }, [userRole, organizationId, selectedOrganizationIds, fetchClustersForFilter]); 

  // Fetch report data when filters change
  useEffect(() => {
    fetchReportData();
  }, [fetchReportData]); // fetchReportData includes all filter dependencies

  const handleExportCSV = () => {
    if (!summaryData || summaryData.length === 0) {
      console.log('No summary data to export.');
      // Optionally show an alert to the user
      // setError('No data available to export as CSV.');
      return;
    }

    // Prepare data for CSV
    const csvData = summaryData.map(row => {
      // Recalculate averages for export consistency
      const start = dateRange[0];
      const end = dateRange[1];
      const totalDaysInRange = (start && end) 
          ? Math.max(1, Math.round((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1)
          : 1; 
      const avgDailyData = row.total_sim_days > 0 ? (row.total_data_consumption / row.total_sim_days) * totalDaysInRange : 0;
      const avgDailyAirtime = row.total_sim_days > 0 ? (row.total_airtime_consumption / row.total_sim_days) * totalDaysInRange : 0;

      return {
        'Cluster Name': row.cluster_name,
        'Total Data Consumption (MB)': row.total_data_consumption.toFixed(2),
        'Total Airtime Consumption (R)': row.total_airtime_consumption.toFixed(2),
        'Average Daily Data (MB)': avgDailyData.toFixed(2),
        'Average Daily Airtime (R)': avgDailyAirtime.toFixed(2),
        'Latest SIM Count': row.sim_count_latest,
      };
    });

    const csv = unparse(csvData);

    // Create and trigger download
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    // Construct filename with date range and period type for clarity
    const startDateStr = dateRange[0]?.toISOString().split('T')[0] || 'start';
    const endDateStr = dateRange[1]?.toISOString().split('T')[0] || 'end';
    const filename = `usage_summary_${startDateStr}_to_${endDateStr}.csv`;
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url); // Clean up
    console.log('CSV export initiated.');
  };

  // Handler for Organization Change (Superadmin only)
  const handleOrganizationChange = (event: SelectChangeEvent<string[]>) => {
      const { target: { value } } = event;
      setSelectedOrganizationIds(
          typeof value === 'string' ? value.split(',') : value,
      );
      // When orgs change, cluster selection might become invalid, so clear it
      // setSelectedClusterIds([]); // Handled by useEffect dependency now
  };

  const handleClusterChange = (event: SelectChangeEvent<string[]>) => {
    const { target: { value } } = event;
    setSelectedClusterIds(
      typeof value === 'string' ? value.split(',') : value,
    );
  };

  const handleDateChange = (newDateRange: [Date | null, Date | null]) => {
    if (newDateRange[0] && newDateRange[1] && newDateRange[0] > newDateRange[1]) {
      setError("Start date cannot be after end date.");
      return;
    }
    setDateRange(newDateRange);
    setSelectedPreset('custom');
  };

  const handlePresetChange = (event: SelectChangeEvent<PresetRange>) => {
    const preset = event.target.value as PresetRange;
    setSelectedPreset(preset);

    if (preset === 'custom') return;

    let start: Date | null = null;
    let end: Date | null = null;
    const now = new Date();

    switch (preset) {
        case 'today':
            start = startOfDay(now);
            end = endOfDay(now);
            break;
        case 'yesterday':
            start = startOfDay(subDays(now, 1));
            end = endOfDay(subDays(now, 1));
            break;
        case 'last7days':
            start = startOfDay(subDays(now, 6));
            end = endOfDay(now);
            break;
        case 'last30days':
            start = startOfDay(subDays(now, 29));
            end = endOfDay(now);
            break;
        case 'thisWeekSun':
            start = startOfWeek(now, { weekStartsOn: 0 });
            end = endOfDay(now);
            break;
        case 'lastWeekSun':
            start = startOfWeek(subDays(now, 7), { weekStartsOn: 0 });
            end = endOfWeek(subDays(now, 7), { weekStartsOn: 0 });
            break;
        case 'thisWeekMon':
            start = startOfWeek(now, { weekStartsOn: 1 });
            end = endOfDay(now);
            break;
        case 'lastWeekMon':
            start = startOfWeek(subDays(now, 7), { weekStartsOn: 1 });
            end = endOfWeek(subDays(now, 7), { weekStartsOn: 1 });
            break;
        case 'thisMonth':
            start = startOfMonth(now);
            end = endOfDay(now);
            break;
        case 'lastMonth':
            const lastMonthStart = startOfMonth(subMonths(now, 1));
            start = lastMonthStart;
            end = endOfMonth(lastMonthStart);
            break;
        case 'thisYear':
            start = startOfYear(now);
            end = endOfDay(now);
            break;
        case 'lastYear':
             const lastYearStart = startOfYear(subYears(now, 1));
            start = lastYearStart;
            end = endOfYear(lastYearStart);
            break;
    }

    if (start && end) {
        setDateRange([start, end]);
    } else {
        setDateRange([null, null]); 
    }
  };

  const CustomTooltip = ({ active, payload, label }: TooltipProps<ValueType, NameType>) => {
    if (active && payload && payload.length) {
      const dataValue = payload[0]?.value;
      const airtimeValue = payload[1]?.value;

      const dataText = typeof dataValue === 'number' ? dataValue.toFixed(2) : 'N/A';
      const airtimeText = typeof airtimeValue === 'number' ? airtimeValue.toFixed(2) : 'N/A';

      return (
        <Paper elevation={3} sx={{ p: 1.5, backgroundColor: 'rgba(255, 255, 255, 0.9)' }}>
          <Typography variant="body2" sx={{ mb: 1 }}>{`Period starting: ${label}`}</Typography>
          <Typography variant="body2" color="#8884d8">{`Data: ${dataText} MB`}</Typography>
          <Typography variant="body2" color="#82ca9d">{`Airtime: R${airtimeText}`}</Typography>
        </Paper>
      );
    }
    return null;
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Usage Trends Report
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleExportCSV}
          >
            Export CSV
          </Button>
        </Box>
      </Box>

      {/* Filters Row */}
      <Paper sx={{ p: 2, mb: 3, display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
        {/* Organization Filter (Superadmin only) */}
        {userRole === 'superadmin' && (
             <FormControl sx={{ width: 300 }}>
                <InputLabel id="org-multi-select-label">Organizations</InputLabel>
                <Select
                    labelId="org-multi-select-label"
                    multiple
                    value={selectedOrganizationIds}
                    onChange={handleOrganizationChange}
                    input={<OutlinedInput label="Organizations" />}
                    renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {selected.length === 0 && <em>All Organizations</em>}
                            {allOrganizations
                                .filter(org => selected.includes(org.id))
                                .map((org) => (
                                    <Chip key={org.id} label={org.name} size="small" />
                                ))}
                        </Box>
                    )}
                    MenuProps={MenuProps}
                >
                    {allOrganizations.length === 0 && <MenuItem disabled>Loading organizations...</MenuItem>}
                    {allOrganizations.map((org) => (
                        <MenuItem key={org.id} value={org.id}>
                            <Checkbox checked={selectedOrganizationIds.indexOf(org.id) > -1} />
                            <ListItemText primary={org.name} />
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
        )}

        {/* Cluster Selector - Moved Here */}
        <FormControl sx={{ width: 300 }}>
          <InputLabel id="cluster-multi-select-label">Clusters</InputLabel>
          <Select
            labelId="cluster-multi-select-label"
            multiple
            value={selectedClusterIds}
            onChange={handleClusterChange}
            input={<OutlinedInput label="Clusters" />}
            renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.length === 0 && <em>All Clusters</em>}
                  {allClusters
                    .filter(c => selected.includes(c.id))
                    .map((cluster) => (
                      <Chip key={cluster.id} label={cluster.name} size="small" />
                    ))}
                </Box>
              )}
            MenuProps={MenuProps}
          >
            {allClusters.length === 0 && <MenuItem disabled>Loading clusters...</MenuItem>}
            {allClusters.map((cluster) => (
              <MenuItem key={cluster.id} value={cluster.id}>
                <Checkbox checked={selectedClusterIds.indexOf(cluster.id) > -1} />
                <ListItemText primary={cluster.name} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Preset Range Dropdown */}
        <FormControl sx={{ minWidth: 180 }}>
            <InputLabel id="preset-range-label">Date Range</InputLabel>
            <Select
                labelId="preset-range-label"
                value={selectedPreset}
                label="Date Range"
                onChange={handlePresetChange}
            >
                <MenuItem value="custom">Custom Range</MenuItem>
                <MenuItem value="today">Today</MenuItem>
                <MenuItem value="yesterday">Yesterday</MenuItem>
                <MenuItem value="last7days">Last 7 Days</MenuItem>
                <MenuItem value="last30days">Last 30 Days</MenuItem>
                <MenuItem value="thisWeekSun">This Week (Sun-Today)</MenuItem>
                <MenuItem value="lastWeekSun">Last Week (Sun-Sat)</MenuItem>
                <MenuItem value="thisWeekMon">This Week (Mon-Today)</MenuItem>
                <MenuItem value="lastWeekMon">Last Week (Mon-Sun)</MenuItem>
                <MenuItem value="thisMonth">This Month</MenuItem>
                <MenuItem value="lastMonth">Last Month</MenuItem>
                <MenuItem value="thisYear">This Year</MenuItem>
                <MenuItem value="lastYear">Last Year</MenuItem>
            </Select>
        </FormControl>

        {/* Date Pickers */}
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box sx={{ display: 'flex', gap: 2 }}>
                <DatePicker
                  label="Start Date"
                  value={dateRange[0]}
                  onChange={(newValue) => handleDateChange([newValue, dateRange[1]])}
                  slotProps={{ textField: { size: 'small' } }}
                />
                <DatePicker
                  label="End Date"
                  value={dateRange[1]}
                  onChange={(newValue) => handleDateChange([dateRange[0], newValue])}
                  minDate={dateRange[0] ?? undefined}
                  slotProps={{ textField: { size: 'small' } }}
                />
            </Box>
        </LocalizationProvider>

      </Paper>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
      )}

      <Paper sx={{ p: 2, mb: 3, height: 400 }}>
          <Typography variant="h6" component="h2" sx={{ mb: 2 }}>
             Consumption Trend (Selected Clusters)
          </Typography>
          {loading ? (
             <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <CircularProgress />
             </Box>
          ) : chartData.length > 0 ? (
             <ResponsiveContainer width="100%" height="90%">
                <LineChart 
                    data={chartData} 
                    margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="period_start_formatted" />
                    <YAxis 
                        yAxisId="left" 
                        label={{ value: 'Data (MB)', angle: -90, position: 'insideLeft' }} 
                    />
                    <YAxis 
                        yAxisId="right" 
                        orientation="right" 
                        label={{ value: 'Airtime (R)', angle: -90, position: 'insideRight' }} 
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Line 
                        yAxisId="left" 
                        type="monotone" 
                        dataKey="total_data" 
                        name="Total Data" 
                        stroke="#8884d8"
                        strokeWidth="3"
                        activeDot={{ r: 8 }} 
                    />
                    <Line 
                        yAxisId="right" 
                        type="monotone" 
                        dataKey="total_airtime" 
                        name="Total Airtime" 
                        stroke="#82ca9d"
                        strokeWidth="3" 
                    />
                </LineChart>
             </ResponsiveContainer>
           ) : (
             <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <Typography variant="body1" color="textSecondary">No data available for chart with selected filters.</Typography>
          </Box>
        )}
      </Paper>

      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <Typography variant="h6" sx={{ p: 2 }}>Summary by Cluster</Typography>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
              <TableContainer>
            <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                  <TableCell>Cluster</TableCell>
                  <TableCell align="right">Total Data (MB)</TableCell>
                  <TableCell align="right">Total Airtime (R)</TableCell>
                  <TableCell align="right">Avg Daily Data (MB)</TableCell>
                  <TableCell align="right">Avg Daily Airtime (R)</TableCell>
                  <TableCell align="right">Latest SIM Count</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                {summaryData.length === 0 ? (
                      <TableRow>
                    <TableCell colSpan={6} align="center">
                      No usage data found for the selected filters.
                        </TableCell>
                      </TableRow>
                    ) : (
                  summaryData.map((row) => {
                    const start = dateRange[0];
                    const end = dateRange[1];
                    const totalDaysInRange = (start && end) 
                        ? Math.max(1, Math.round((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1)
                        : 1;
                    const avgDailyData = row.total_sim_days > 0 ? (row.total_data_consumption / row.total_sim_days) * totalDaysInRange : 0;
                    const avgDailyAirtime = row.total_sim_days > 0 ? (row.total_airtime_consumption / row.total_sim_days) * totalDaysInRange : 0;

                    return (
                        <TableRow hover key={row.cluster_id}> 
                            <TableCell>{row.cluster_name}</TableCell>
                            <TableCell align="right">{row.total_data_consumption.toFixed(2)}</TableCell>
                            <TableCell align="right">{row.total_airtime_consumption.toFixed(2)}</TableCell>
                            <TableCell align="right">{avgDailyData.toFixed(2)}</TableCell>
                            <TableCell align="right">{avgDailyAirtime.toFixed(2)}</TableCell>
                            <TableCell align="right">{row.sim_count_latest}</TableCell>
                        </TableRow>
                    );
                })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
        )}
      </Paper>

    </Container>
  );
} 